/**
 * This is the dropdown list widget
 *
 * optional @prop widgetId {String} widget ID
 * optional @prop type {String} Type of the widget
 *                valid types are small or medium
 * optional @prop onSelectedValue {Func} what to do when a user selects a list item
 * optional @prop onClickOutside {Func} what to do when a user clicks outside of the dropdown
 * optional @prop reset {Bool} ignore alreadyDelegated variable
 * optional @prop list {Array} List of dropdown menu items
 *          expected structure is [{
 *            id {String} ID,
 *            value {String|node} value,
 *            @optional forceDisabled {Bool},
 *            @optional submenu {Array} [{ id: ID, value: value, ... }]
 *          }, ... ]
 *
 * return widgetID: {String} widget ID
 *        selectedElemID: {String} selected elem ID
 */
/* globals window */
import React from 'react';
import PropTypes from 'prop-types';

import { findDOMNode } from 'react-dom';
import { omit } from 'lodash';
import { killEvent } from 'event-helpers';
import displayName from 'component-lib/displayName';

import DropdownBase from './DropdownBase';

import styles from './Dropdowns.scss';

export default class DropdownList extends DropdownBase {
    static displayName = displayName('Widgets/Dropdowns/DropdownList');

    static propTypes = {
        ...DropdownBase.propTypes,
        onClickOutside: PropTypes.func,
    };

    componentDidMount() {
        this.eatInitialClick = true;

        window.addEventListener('click', this.handleOutsideClick);
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.handleOutsideClick);
    }

    handleOutsideClick = (e) => {
    // the initial click to open this dropdown actually makes it to
    // the menu. eat the first click that we get. if we start to run
    // into issues, we can re-evaluate this.
    /* eslint-disable react/no-find-dom-node */
        if (this.eatInitialClick) {
            this.eatInitialClick = false;
        } else if (this.props.onClickOutside && !findDOMNode(this).contains(e.target)) {
            this.props.onClickOutside();
        }
    /* eslint-enable react/no-find-dom-node */
    };

    setListsVisibilities = ({ target }) => {
        const $target = $(target);
        const $targetListElem = $target.attr('data-status') ? $target : $target.closest('li');

        if (
            $targetListElem.length &&
      $targetListElem.attr('data-status') &&
      $targetListElem.attr('data-status') === 'closed'
        ) {
            $targetListElem
                .siblings('[data-status="opened"]')
                .attr('data-status', 'closed')
                .find('ol')
                .css({ display: 'none' })
                .find('[data-status="opened"]')
                .attr('data-status', 'closed');
        }

        $targetListElem
            .attr('data-status', 'opened')
            .children('ol')
            .css({ display: 'block' });
    };

    onItemClick = (listElemId, forceDisabled = false) => (e) => {
        if (!this.alreadyDelegated || this.props.reset) {
            if (forceDisabled) {
                killEvent(e);
            } else {
                this.props.onSelectedValue(this.props.widgetId, listElemId);
                this.alreadyDelegated = true;
            }
        }
    };

    renderDropdownList(list, classType = 'list') {
        return (
            <ol data-list-container className={styles[`dropdown-${classType}-container`]}>
                {list.map(({ id, value, forceDisabled = false, submenu = false }, idx) => {
                    const isForceDisabled = forceDisabled || submenu;
                    let additionalTags = {};
                    let className = 'list-elem';

                    if (forceDisabled) className += '-disabled';

                    if (submenu) {
                        additionalTags = {
                            'data-inactive': false,
                            'data-has-submenu': true,
                        };
                    }

                    return (
                        <li
                            role="menuitem"
                            {...additionalTags}
                            key={id}
                            ref={(c) => {
                                this[`listElem${idx}`] = c;
                            }}
                            onClick={this.onItemClick(id, isForceDisabled)}
                            className={styles[className]}
                            data-action={`select-${id}`}
                            onMouseOver={this.setListsVisibilities}
                            data-status="closed"
                        >
                            <data className={styles['list-value']}>
                                {value}
                                {submenu && <data className={styles['arrow-icon']} />}
                            </data>
                            {submenu && this.renderDropdownList(submenu, 'submenu')}
                        </li>
                    );
                })}
            </ol>
        );
    }

    render() {
        return (
            <div
                ref={(c) => {
                    this.widgetContainer = c;
                }}
                {...omit(this.getWidgetProps(), 'widgetId', 'onClickOutside', 'reset', 'prefixText')}
                data-field={`dropdown-list-${this.props.widgetId}`}
            >
                {this.renderDropdownList(this.props.list)}
            </div>
        );
    }
}
